import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
} from "@mui/material";

const formatNumber = (val) =>
  Number(val || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const InvoiceDetailsTab = ({ invoice }) => {
  const { header, items, payments, taxBreakdowns } = invoice;

  return (
    <Grid container spacing={3}>
      {/* LEFT COLUMN */}
      <Grid item xs={12} md={4}>
        <Stack spacing={3}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Billed To
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Customer Name
              </Typography>
              <Typography variant="body1" fontWeight="bold" gutterBottom>
                {invoice.customerBusinessName}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
                sx={{ mt: 2 }}
              >
                Contact Email
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {invoice.customerEmail || "Not Provided"}
              </Typography>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Issued By
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                {header?.companySnapshot?.companyName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PIN: {header?.companySnapshot?.taxId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {header?.companySnapshot?.address}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Tel: {header?.companySnapshot?.phone}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Email: {header?.companySnapshot?.email}
              </Typography>
            </CardContent>
          </Card>
        </Stack>
      </Grid>

      {/* RIGHT COLUMN */}
      <Grid item xs={12} md={8}>
        <Card
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            mb: 3,
            bgcolor: "background.paper",
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Invoice Items
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: "background.default" }}>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell align="right">Tax Amount</TableCell>
                    <TableCell align="right">Line Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items?.length > 0 ? (
                    items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {item.name}
                          </Typography>
                          {item.taxCode && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Tax Code: {item.taxCode}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{item.qty}</TableCell>
                        <TableCell align="right">
                          {formatNumber(item.price)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: "error.main" }}>
                          {item.discount > 0
                            ? formatNumber(item.discount)
                            : "-"}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: "text.secondary" }}
                        >
                          {formatNumber(item.taxAmount)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {formatNumber(item.amount)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No items found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                height: "100%",
                bgcolor: "background.paper",
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Payment History
                </Typography>
                {payments?.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {payments.map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell sx={{ borderBottom: "none", pl: 0 }}>
                              <Typography variant="body2" fontWeight="bold">
                                {payment.paymentMethodName}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {new Date(payment.createdAt).toLocaleString()}
                              </Typography>
                              {payment.referenceCode && (
                                <Typography
                                  variant="caption"
                                  color="secondary.main"
                                >
                                  Ref: {payment.referenceCode}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                borderBottom: "none",
                                pr: 0,
                                fontWeight: "bold",
                                color: "success.main",
                              }}
                            >
                              {formatNumber(payment.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    No payments recorded.
                  </Typography>
                )}

                {taxBreakdowns?.length > 0 && (
                  <Box
                    mt={3}
                    pt={2}
                    borderTop="1px dashed rgba(255,255,255,0.1)"
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                      fontWeight="bold"
                    >
                      Tax Breakdown
                    </Typography>
                    {taxBreakdowns.map((tax) => (
                      <Box
                        key={tax.id}
                        display="flex"
                        justifyContent="space-between"
                        mb={0.5}
                      >
                        <Typography variant="body2">
                          {tax.name} ({tax.rate}%)
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {formatNumber(tax.taxAmount)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
                height: "100%",
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Summary
                </Typography>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  mb={1}
                  mt={2}
                >
                  <Typography variant="body2" color="text.secondary">
                    Subtotal
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {formatNumber(invoice.subtotal)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Total Discount
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="500"
                    color="error.main"
                  >
                    - {formatNumber(invoice.totalDiscount || 0)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Total Tax
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {formatNumber(invoice.totalTax)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" fontWeight="bold">
                    Invoice Total
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="secondary.main"
                  >
                    {formatNumber(invoice.total)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Amount Paid
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color="success.main"
                  >
                    {formatNumber(invoice.amountPaid)}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  p={1}
                  bgcolor="background.paper"
                  borderRadius={1}
                  mt={1}
                >
                  <Typography variant="body2" fontWeight="bold">
                    Balance Due
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={
                      invoice.total - invoice.amountPaid > 0
                        ? "error.main"
                        : "text.primary"
                    }
                  >
                    {formatNumber(invoice.total - invoice.amountPaid)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
export default InvoiceDetailsTab;
